import "./css/employeemanage.css";
import {useEffect, useState} from "react";
import EmployeeRegisterModal from "./modal/EmployeeRegisterModal";
import EmployeeModModal from "./modal/EmployeeModModal";
import { apiDelete, apiGet } from "../../utils/apiClient";

const EmployeeManage = () => {
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isModModalOpen, setIsModModalOpen] = useState(false);
  const [memberData, setMemberData] = useState([]);

  // 직원 데이터 새로고침 함수
  const fetchMemberData = async () => {
    try {
      const userInfo = localStorage.getItem("user");
      const storeCode = JSON.parse(userInfo).storeCode;

      const response = await apiGet(`/store/member/view/${storeCode}`);

      if (!response.ok) {
        throw new Error("네트워크 응답에 문제가 있습니다.");
      }
      const data = await response.json();
      setMemberData(data);
    } catch (error) {
      console.error("직원 데이터 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchMemberData();
  }, []);

  // 등록 모달 열기
  const handleRegister = () => {
    setSelectedMember(null);
    setIsRegisterModalOpen(true);
  };

  // 수정 모달 열기
  const handleEdit = (member) => {
    setSelectedMember(member);
    setIsModModalOpen(true);
  };

  // 삭제 처리
  const handleDelete = async (member) => {
    if (!window.confirm(`${member.memberNm} 직원을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await apiDelete(`/store/member/${member.memberId}`);

      if (response.ok) {
        alert("직원이 성공적으로 삭제되었습니다.");
        await fetchMemberData();
      } else {
        const errorText = await response.text();
        alert(`삭제 실패: ${errorText}`);
      }
    } catch (error) {
      console.error("삭제 실패:", error);
      alert("직원 삭제 중 오류가 발생했습니다.");
    }
  };

  // 권한 정보를 한글로 변환
  const formatAuthInfo = (menuAuth) => {
    if (!menuAuth || !Array.isArray(menuAuth) || menuAuth.length === 0) return "-";
    
    const displayNames = menuAuth.map(auth => auth.displayName).filter(Boolean);
    
    return displayNames.length > 0 ? displayNames.join(", ") : "권한 없음";
  };

  // 직원 타입을 한글로 변환
  const formatMemberType = (memberType) => {
    const typeLabels = {
      "OWNER": "점주",
      "MANAGER": "매니저"
    };
    
    return typeLabels[memberType] || memberType;
  };

  // 직원 통계 계산
  const ownerCount = memberData.filter(m => m.memberType === "점주").length;
  const managerCount = memberData.filter(m => m.memberType === "매니저").length;

  // 최대 3명까지 카드 배열 생성 (기존 직원 + 빈 슬롯)
  const maxEmployees = 3;
  const emptySlots = maxEmployees - memberData.length;
  const allCards = [...memberData];
  
  // 빈 슬롯 추가
  for (let i = 0; i < emptySlots; i++) {
    allCards.push({ isEmpty: true, slotIndex: i });
  }

  return (
    <div className="empmanage-wrap">
      <div className="empmanage-header">
        <div className="empmanage-header-title">직원관리</div>
      </div>

      <div className="empmanage-card-container">
        {allCards.map((p, idx) => {
          // 빈 슬롯인 경우
          if (p.isEmpty) {
            return (
              <div 
                key={`empty-${p.slotIndex}`} 
                className="empmanage-card empmanage-card-empty"
                onClick={handleRegister}
              >
                <div className="empmanage-card-empty-content">
                  <div className="empmanage-card-empty-icon">+</div>
                  <div className="empmanage-card-empty-text">직원 등록</div>
                </div>
              </div>
            );
          }
          
          // 기존 직원 카드
          return (
            <div key={p.memberIdx} className="empmanage-card">
              <div className="empmanage-card-header">
                <div className="empmanage-card-name">{p.memberNm}</div>
                <div className="empmanage-card-type">{formatMemberType(p.memberType)}</div>
              </div>
              <div className="empmanage-card-body">
                <div className="empmanage-card-info">{p.memberTelNo}</div>
                <div className="empmanage-card-info">{p.memberId}</div>
                <div className="empmanage-card-auth">{formatAuthInfo(p.menuAuth)}</div>
              </div>
              {p.memberType !== "OWNER" && (
                <div className="empmanage-card-footer">
                  <button
                    className="empmanage-card-btn"
                    onClick={() => handleEdit(p)}
                  >정보수정</button>
                  <button 
                    className="empmanage-card-btn"
                    onClick={() => handleDelete(p)}
                  >삭제</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="empmanage-summary-section">
        <div className="empmanage-summary-box">
          <div className="empmanage-summary-title">직원 현황 요약</div>
          <div className="empmanage-summary-content">
            <div className="empmanage-summary-item">
              <span className="empmanage-summary-label">현재 등록된 직원:</span>
              <span className="empmanage-summary-value">{memberData.length}명 / 최대 3명</span>
            </div>
            <div className="empmanage-summary-item">
              <span className="empmanage-summary-label">점주:</span>
              <span className="empmanage-summary-value">{ownerCount}명</span>
              <span className="empmanage-summary-separator">|</span>
              <span className="empmanage-summary-label">매니저:</span>
              <span className="empmanage-summary-value">{managerCount}명</span>
            </div>
          </div>
        </div>

        <div className="empmanage-tips-box">
          <div className="empmanage-tips-title">직원 관리 팁</div>
          <div className="empmanage-tips-content">
            <div className="empmanage-tips-section">
              <div className="empmanage-tips-subtitle">권한 별 기능 요약</div>
              <ul className="empmanage-tips-list">
                <li>직원은 점주 포함 최대 3명까지 등록할 수 있습니다.</li>
                <li>매니저는 점주가 부여한 권한 내에서만 메뉴 접근이 가능합니다.</li>
                <li>직원 삭제 시 즉시 삭제됩니다.</li>
                <li>점주 계정은 수정 및 삭제가 불가능합니다.</li>
              </ul>
            </div>
            <div className="empmanage-tips-section">
              <div className="empmanage-tips-subtitle">권한 설명</div>
              <ul className="empmanage-tips-list">
                <li>알림: 주문, 네고, 리뷰, 문의 등 알림 수신 및 확인 권한</li>
                <li>매출 관리: 주문 목록 조회, 판매 유형별 현황 확인, 취소 요청 처리 권한</li>
                <li>상품 관리: 상품 등록, 수정, 삭제, 할인 등록, 네고 등록 권한</li>
                <li>이력: 할인 및 네고 이력 조회 권한</li>
                <li>고객센터: 공지사항 열람, 문의(FAQ) 등록 권한</li>
                <li>매출 통계: 기간별 매출 통계 및 판매 분석 데이터 조회 권한</li>
                <li>리뷰 관리: 고객 리뷰 조회 및 답변 권한</li>
                <li>마이페이지: 가맹점 기본 정보(영업시간, 연락처 등) 수정 권한</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="empmanage-modal-wrap">
        
        {/* 등록 모달 */}
        {isRegisterModalOpen && (
          <EmployeeRegisterModal
            onClose={() => setIsRegisterModalOpen(false)}
            refreshData={fetchMemberData}
          />
        )}

        {/* 수정 모달 */}
        {isModModalOpen && selectedMember && (
          <EmployeeModModal
            onClose={() => setIsModModalOpen(false)}
            member={selectedMember}
            refreshData={fetchMemberData}
          />
        )}
      </div>
    </div>
  );
}

export default EmployeeManage;
